import React, { useEffect, useState } from 'react';
import { Check, X, ShieldCheck, Truck, Clock, Star } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { TestimonialCard } from '../components/TestimonialCard';
import { FAQItem } from '../components/FAQItem';
import { StickyCTA } from '../components/StickyCTA';
import { useUserCity } from '../hooks/useUserCity';
export function AdvertorialPage() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const {
    city,
    countryCode,
    loading
  } = useUserCity('A');
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToOffer = () => {
    window.location.href = 'https://www.tryglisera.com/products/glisera%E2%84%A2-nasalshield';
  };
  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center" data-id="element-50">
        <div className="flex flex-col items-center gap-3" data-id="element-51">
          <div className="w-8 h-8 border-3 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" data-id="element-52" />
          <span className="text-sm text-slate-400" data-id="element-53">Loading article...</span>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-emerald-100 selection:text-emerald-900" data-id="element-54">
      {/* Navigation / Header Bar */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-40" data-id="element-55">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between" data-id="element-56">
          <div className="font-bold text-xl tracking-tight text-slate-900" data-id="element-57">
            Family Health<span className="text-emerald-600" data-id="element-58"> Today</span>
          </div>
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wider border border-slate-200 px-2 py-1 rounded" data-id="element-59">
            Advertorial
          </div>
        </div>
      </header>

      {/* Main Content with Sidebar Layout */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12" data-id="element-60">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8" data-id="element-61">
          {/* Main Article Content */}
          <main data-id="element-62">
            {/* Article Meta */}
            <div className="mb-8 border-b border-slate-200 pb-8" data-id="element-63">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-4 flex-wrap" data-id="element-64">
                <span className="font-bold text-emerald-700 uppercase tracking-wide text-xs" data-id="element-65">
                  {countryCode} | Family Health
                </span>
                <span data-id="element-66">•</span>
                <span data-id="element-67">February 18, 2025</span>
                <span data-id="element-68">•</span>
                <span data-id="element-69">247,891 views</span>
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight mb-6 tracking-tight" data-id="element-70">
                Why Do {city} Moms Always Get Sick Last—And Hardest?
                Pediatrician Reveals The Real Reason (And The 15-Second Fix)
              </h1>

              <p className="text-lg md:text-xl text-slate-600 italic mb-6" data-id="element-71">
                After 8 winters of getting hit harder than anyone else in the
                house, she discovered the simple morning routine that finally
                broke the cycle.
              </p>

              <div className="flex items-center gap-3" data-id="element-72">
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden" data-id="element-73">
                  <img src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=100&h=100" alt="Dr. Sarah Mitchell" className="w-full h-full object-cover" data-id="element-74" />
                </div>
                <div data-id="element-75">
                  <div className="font-bold text-slate-900 text-sm" data-id="element-76">
                    By Dr. Sarah Mitchell, MD
                  </div>
                  <div className="text-slate-500 text-xs" data-id="element-77">
                    Family Health Today Staff
                  </div>
                </div>
              </div>
            </div>

            {/* Article Body */}
            <article className="prose prose-lg prose-slate max-w-none font-serif" data-id="element-78">
              {/* SECTION 1 - Opening */}
              <p className="lead text-xl md:text-2xl text-slate-700 leading-relaxed font-medium mb-8" data-id="element-79">
                Every winter, the same thing happens.
              </p>

              <p data-id="element-80">One kid gets sick at school.</p>

              <p data-id="element-81">Then the other kid.</p>

              <p data-id="element-82">Then your husband.</p>

              <p data-id="element-83">And finally... you.</p>

              <p className="font-bold text-slate-900" data-id="element-84">
                But here's the worst part.
              </p>

              <p data-id="element-85">
                You always get it last. And you always get it <em data-id="element-86">hardest</em>.
              </p>

              <p data-id="element-87">
                While everyone else recovers in 3 days, you're down for a week.
                Or two.
              </p>

              <p data-id="element-88">
                Your throat feels like glass. Your head pounds. You can barely
                get out of bed.
              </p>

              <p data-id="element-89">But you still have to.</p>

              <p data-id="element-90">
                Because who else is going to make lunches? Who else is going to
                run the house?
              </p>

              <p className="font-bold text-slate-900" data-id="element-91">
                There's no sick leave for moms.
              </p>

              <img src="https://cdn.magicpatterns.com/uploads/88PBCjBuRMpWbov9ppCHtJ/ChatGPT_Image_17_Φεβ_2026,_09_26_02_μ.μ..png" alt="Exhausted mom on the couch while kids play around her" className="w-full rounded-xl my-8 shadow-lg" data-id="element-92" />

              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 md:p-6 my-8 not-prose rounded-r-lg" data-id="element-93">
                <p className="font-serif text-base md:text-lg text-emerald-900 italic font-medium m-0" data-id="element-94">
                  "I catch everything my kids bring home. And I get it way worse
                  than they do. It's like I'm the last domino to fall... and I
                  fall the hardest." — Jessica M., Mom of 2
                </p>
              </div>

              <p data-id="element-95">If this sounds familiar, you're not alone.</p>

              <p data-id="element-96">Millions of moms go through this every single winter.</p>

              <p data-id="element-97">And most of them blame themselves.</p>

              <p data-id="element-98">
                <em data-id="element-99">"Maybe I'm just run down."</em>
              </p>

              <p data-id="element-100">
                <em data-id="element-101">"Maybe I need more sleep."</em>
              </p>

              <p data-id="element-102">
                <em data-id="element-103">"Maybe my immune system is shot."</em>
              </p>

              <p data-id="element-104">But here's what most moms don't know.</p>

              <p className="font-bold text-slate-900" data-id="element-105">It's not your fault.</p>

              <p data-id="element-106">And it's definitely not your immune system.</p>

              <p data-id="element-107">
                The real reason you keep getting sick has nothing to do with how
                strong or weak you are.
              </p>

              <p data-id="element-108">
                It has everything to do with{' '}
                <strong data-id="element-109">where germs enter your body.</strong>
              </p>

              {/* SECTION 2 - The Front Door Problem */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-110">
                The Front Door Problem
              </h2>

              <p data-id="element-111">Here's what actually happens when your kid comes home sick.</p>

              <p data-id="element-112">
                They're covered in germs. Especially on their hands. And in
                their nose.
              </p>

              <p data-id="element-113">
                When they talk, laugh, cough, or sneeze... those germs go into
                the air.
              </p>

              <p className="font-bold text-slate-900" data-id="element-114">
                And 97% of them enter your body through your nose.
              </p>

              <img src="https://cdn.magicpatterns.com/uploads/aZbyaP6tLJ9qd6kwQexumq/ChatGPT_Image_17_Φεβ_2026,_09_23_33_μ.μ..png" alt="Child sneezing next to concerned mom" className="w-full rounded-xl my-8 shadow-lg" data-id="element-115" />

              <p data-id="element-116">Think about it.</p>

              <p data-id="element-117">
                You've tried elderberry. Zinc. Vitamin D. Maybe even expensive
                immune gummies.
              </p>

              <p data-id="element-118">
                But all of those work <em data-id="element-119">inside</em> your bloodstream.
              </p>

              <p data-id="element-120">
                They boost your immune system <em data-id="element-121">after</em> germs are already
                in your body.
              </p>

              <p className="font-bold text-slate-900" data-id="element-122">
                It's like locking your back door... while leaving the front door
                wide open.
              </p>

              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 md:p-6 my-8 not-prose rounded-r-lg" data-id="element-123">
                <p className="font-sans font-bold text-emerald-800 text-sm uppercase tracking-wide mb-2" data-id="element-124">
                  📌 The Nasal Gateway
                </p>
                <p className="font-serif text-base md:text-lg text-emerald-900 italic font-medium m-0" data-id="element-125">
                  Your nose is the front door for germs. If you're only taking
                  supplements that work in your bloodstream, you're fighting the
                  battle after it's already too late.
                </p>
              </div>

              <p data-id="element-126">
                By the time your immune system kicks in, the virus has already
                set up camp.
              </p>

              <p data-id="element-127">And that's why you get hit so hard.</p>

              <p data-id="element-128">
                Your body is playing catch-up. But the germs got a head start.
              </p>

              {/* SECTION 3 - The Pediatrician Who Had Enough */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-129">
                The Pediatrician Who Had Enough
              </h2>

              <p data-id="element-130">Dr. Lisa Chen is a pediatrician in Portland.</p>

              <p data-id="element-131">And a mom of three.</p>

              <p data-id="element-132">For years, she dealt with the same cycle.</p>

              <p data-id="element-133">
                Her kids brought home every cold, flu, and virus from school.
              </p>

              <p data-id="element-134">
                And even though she knew how the immune system worked, she
                couldn't stop getting sick.
              </p>

              <img src="https://cdn.magicpatterns.com/uploads/3cSxaWeDWZTn8TANMjMszx/ChatGPT_Image_17_Φεβ_2026,_09_26_01_μ.μ..png" alt="Female doctor and mom Dr. Lisa Chen" className="w-full rounded-xl my-8 shadow-lg" data-id="element-135" />

              <p data-id="element-136">
                "I was taking all the right supplements," she says. "Vitamin C,
                zinc, elderberry. I was doing everything by the book."
              </p>

              <p data-id="element-137">
                "But I still got hammered every winter. Usually worse than my
                kids."
              </p>

              <p data-id="element-138">
                That's when she started looking at the research differently.
              </p>

              <p data-id="element-139">
                She wasn't asking: <em data-id="element-140">"How do I boost my immune system?"</em>
              </p>

              <p data-id="element-141">
                She was asking:{' '}
                <strong data-id="element-142">"How do I stop germs before they get in?"</strong>
              </p>

              <p data-id="element-143">
                And that's when she found something most moms have never heard
                of.
              </p>

              <p data-id="element-144">
                A 100-year-old antiseptic that hospitals have used for decades.
              </p>

              <p data-id="element-145">Not to boost immunity.</p>

              <p className="font-bold text-slate-900" data-id="element-146">
                But to stop germs at the entry point.
              </p>

              {/* SECTION 4 - The Solution Hiding in Plain Sight */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-147">
                The Solution Hiding In Plain Sight
              </h2>

              <p data-id="element-148">
                The answer? <strong data-id="element-149">Iodine.</strong>
              </p>

              <p data-id="element-150">You might know iodine from cuts and scrapes.</p>

              <p data-id="element-151">
                Hospitals have used it for over 100 years to clean wounds and
                stop infection.
              </p>

              <p data-id="element-152">But what most people don't know is this:</p>

              <p className="font-bold text-slate-900" data-id="element-153">
                Iodine works in your nose too.
              </p>

              <p data-id="element-154">
                When you use a gentle iodine nasal spray, it creates a barrier
                right where germs enter.
              </p>

              <p data-id="element-155">It doesn't "boost" your immune system.</p>

              <p className="font-bold text-slate-900" data-id="element-156">
                It stops germs before your immune system even needs to fight.
              </p>

              <p data-id="element-157">Think of it like this:</p>

              <p data-id="element-158">
                Your elderberry and zinc are the security guards <em data-id="element-159">inside</em>{' '}
                your house.
              </p>

              <p data-id="element-160">
                Iodine is the <strong data-id="element-161">lock on your front door.</strong>
              </p>

              <img src="https://cdn.magicpatterns.com/uploads/hawV2HdRncR3ynWaEa8Whv/ChatGPT_Image_17_Φεβ_2026,_09_26_00_μ.μ..png" alt="Nose door with lock illustration" className="w-full rounded-xl my-8 shadow-lg" data-id="element-162" />

              <p data-id="element-163">And recent studies are backing this up.</p>

              <p data-id="element-164">
                A 2025 study found that people who used iodine nasal spray had
                shorter, milder colds when they did get sick.
              </p>

              <p data-id="element-165">
                Another study showed it worked in just{' '}
                <strong data-id="element-166">15 seconds</strong> after contact with germs.
              </p>

              <div className="bg-emerald-50 p-4 md:p-6 rounded-xl border border-emerald-100 my-8 not-prose" data-id="element-167">
                <p className="font-sans font-bold text-emerald-800 text-sm uppercase tracking-wide mb-3" data-id="element-168">
                  Why Iodine Works
                </p>
                <ul className="space-y-3" data-id="element-169">
                  <li className="flex items-start gap-3" data-id="element-170">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-171">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-172" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-173">
                      Stops germs at the entry point (your nose)
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-174">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-175">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-176" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-177">
                      Works in 15 seconds
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-178">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-179">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-180" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-181">
                      Doesn't rely on your immune system
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-182">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-183">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-184" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-185">
                      Safe for daily use
                    </span>
                  </li>
                </ul>
              </div>

              {/* SECTION 5 - "I Didn't Get It. Not Once." */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-186">
                "I Didn't Get It. Not Once."
              </h2>

              <p data-id="element-187">
                After Dr. Chen started using iodine nasal spray herself,
                something changed.
              </p>

              <p data-id="element-188">Her kids still got sick. Her husband still got sick.</p>

              <p className="font-bold text-slate-900" data-id="element-189">But she didn't.</p>

              <p data-id="element-190">
                "I couldn't believe it," she says. "Everyone around me was down
                with the flu. I took care of them all. And I didn't get it. Not
                once."
              </p>

              <img src="https://cdn.magicpatterns.com/uploads/93kaSx8ebkZAS9E3rmjEKT/ChatGPT_Image_17_Φεβ_2026,_09_23_48_μ.μ..png" alt="Healthy mom enjoying tea while family is sick" className="w-full rounded-xl my-8 shadow-lg" data-id="element-191" />

              <p data-id="element-192">She started recommending it to other moms in her practice.</p>

              <p data-id="element-193">And they started seeing the same results.</p>

              <p data-id="element-194">
                That's when she partnered with <strong data-id="element-195">Glisera™</strong> to
                create <strong data-id="element-196">NasalShield</strong>.
              </p>

              <p data-id="element-197">
                A gentle, daily nasal cleansing spray designed specifically for
                the demands of family life.
              </p>

              <img src="https://cdn.magicpatterns.com/uploads/gayLbUVG1Vk5kYjsBs2oBE/ChatGPT_Image_17_Φεβ_2026,_09_25_46_μ.μ..png" alt="Glisera NasalShield nasal spray product" className="w-full rounded-xl my-8 shadow-lg" data-id="element-198" />

              {/* SECTION 6 - Testimonials */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-199">
                What Moms Are Saying
              </h2>

              <div className="not-prose grid gap-6" data-id="element-200">
                <TestimonialCard headline="Haven't been sick in 4 months" quote="I was so tired of being the last one down. I started using Glisera™ NasalShield every morning after the kids leave for school. I haven't been sick in 4 months. Not once." author="Rachel P." location="Mom of 3" avatarUrl="https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-201" />
                <TestimonialCard headline="Everyone got the flu except me" quote="Everyone in my house got the flu in January. My husband was down for a week. Both kids missed school. I used NasalShield twice a day and didn't get it at all. I still can't believe it." author="Michelle K." location="Mom of 2" avatarUrl="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-202" />
                <TestimonialCard headline="Takes 15 seconds. Wish I'd found it years ago." quote="I use it every time I pick the kids up from school. It takes 15 seconds. I wish I'd found this years ago." author="Amanda L." location="Mom of 4" avatarUrl="https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-203" />
                <TestimonialCard headline="Around 4 sick people and didn't catch it" quote="Over Christmas I was around 4 sick people and 2 were kids. I catch everything during winter. This time? Nothing. I'm shocked." author="Jennifer T." location="Mom of 2" avatarUrl="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-204" />
                <TestimonialCard headline="My patient had the flu — I didn't catch it" quote="I use this every day when we get home from gatherings. My patient caught the flu and was down for 4 days. I did not catch it at all." author="Patricia R." location="Nurse & Mom" avatarUrl="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-205" />
                <TestimonialCard headline="Way less congestion. Game changer." quote="I bought this for myself because I get colds easily. I have had way less congestion since starting to use it. Game changer." author="Sarah M." location="Mom of 1" avatarUrl="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-206" />
                <TestimonialCard headline="No fever or throwing up since I started" quote="I haven't thrown up or had a fever since I started using this! I usually cough and it ends up in my chest for 2-3 weeks. Not anymore." author="Lisa K." location="Mom of 2" avatarUrl="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-207" />
                <TestimonialCard headline="Healed in 3 days. This is the real deal." quote="Had a cold, started this spray, healed in 3 days. Usually I can't shake a cold quickly. This is the real deal." author="Maria G." location="Mom of 3" avatarUrl="https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-208" />
                <TestimonialCard headline="Kept me healthy even around sickly kids" quote="I use this before I travel, go to large indoor gatherings, and when I'm feeling run down. I believe it's kept me from getting sick even around sickly kids." author="Donna H." location="Mom of 2" avatarUrl="https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-209" />
                <TestimonialCard headline="Everyone got symptoms except me" quote="Others in my group experienced cold and cough symptoms a few days after the event. I did NOT. I'm a believer now." author="Angela B." location="Mom of 1" avatarUrl="https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=100&h=100&q=80" data-id="element-210" />
              </div>

              {/* Star Rating Block */}
              <div className="not-prose bg-slate-50 rounded-xl p-6 my-8 border border-slate-100 text-center" data-id="element-211">
                <div className="flex items-center justify-center gap-1 mb-2" data-id="element-212">
                  {[...Array(5)].map((_, i) => <Star key={i} size={22} fill="#FCD34D" className="text-yellow-400" data-id="element-213" />)}
                </div>
                <p className="font-bold text-slate-900 text-lg" data-id="element-214">4.8 out of 5</p>
                <p className="text-sm text-slate-600" data-id="element-215">
                  Based on 8,247 customer reviews
                </p>
              </div>

              {/* Mid-article CTA */}
              <div className="not-prose my-10" data-id="element-216">
                <Button fullWidth size="lg" onClick={scrollToOffer} className="shadow-lg shadow-emerald-200" data-id="element-217">
                  <span className="hidden md:inline" data-id="element-218">
                    GET GLISERA™ NASALSHIELD — SAVE 30% NOW →
                  </span>
                  <span className="md:hidden" data-id="element-219">GET NASALSHIELD — 30% OFF →</span>
                </Button>
                <p className="text-center text-xs text-slate-500 mt-2" data-id="element-220">
                  Free Shipping • 60-Day Money-Back Guarantee
                </p>
              </div>

              {/* SECTION 7 - How It Actually Works */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-221">
                How It Actually Works
              </h2>

              <p data-id="element-222">Using Glisera™ NasalShield is simple.</p>

              <p className="font-bold text-slate-900" data-id="element-223">
                Just 2 sprays in each nostril in the morning.
              </p>

              <p data-id="element-224">That's it.</p>

              <p data-id="element-225">Takes 15 seconds.</p>

              <p data-id="element-226">You can do it right after brushing your teeth.</p>

              <p data-id="element-227">
                Some moms also use it after school pickup. Or before going into
                crowded places.
              </p>

              <p data-id="element-228">It's gentle enough for daily use.</p>

              <p data-id="element-229">And it doesn't sting or burn like other nasal sprays.</p>

              <img src="https://cdn.magicpatterns.com/uploads/5iQKnnADWmuzfztk9Pc1j1/ChatGPT_Image_17_Φεβ_2026,_09_49_18_μ.μ..png" alt="Woman using nasal spray in bathroom mirror" className="w-full rounded-xl my-8 shadow-lg" data-id="element-230" />

              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 md:p-6 my-8 not-prose rounded-r-lg" data-id="element-231">
                <p className="font-sans font-bold text-emerald-800 text-sm uppercase tracking-wide mb-2" data-id="element-232">
                  🧼 The Daily Hygiene Habit
                </p>
                <p className="font-serif text-base md:text-lg text-emerald-900 italic font-medium m-0" data-id="element-233">
                  Just like you brush your teeth to stop cavities... you cleanse
                  your nose to stop germs.
                </p>
              </div>

              {/* SECTION 8 - Common Questions */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-234">
                "But Wait... Isn't Iodine Dangerous?"
              </h2>

              <p data-id="element-235">This is the question Dr. Chen hears most often.</p>

              <p data-id="element-236">And it's a smart question.</p>

              <div className="not-prose space-y-2 mb-12" data-id="element-237">
                <FAQItem question="But isn't too much iodine bad for you?" answer="Great question. The amount in Glisera™ NasalShield is tiny. Studies show it doesn't absorb into your bloodstream in significant amounts. It stays local—right where the germs are." data-id="element-238" />
                <FAQItem question="Is this safe around my kids?" answer="Many moms use it themselves and feel more confident staying healthy for their family. Always talk to your pediatrician for children under 12." data-id="element-239" />
                <FAQItem question="I've tried so many things. Why would this work?" answer="Because it's not another immune booster. It's a barrier. A front-line defense. Your supplements work after germs are in. This works before." data-id="element-240" />
              </div>

              {/* SECTION 9 - Why Now Matters */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-241">
                Why Now Matters
              </h2>

              <p data-id="element-242">Here's the thing.</p>

              <p data-id="element-243">We're heading into peak cold and flu season.</p>

              <p data-id="element-244">
                And right now, thousands of moms are ordering Glisera™
                NasalShield every single day.
              </p>

              <p data-id="element-245">Inventory runs low fast during winter months.</p>

              <p data-id="element-246">
                Especially with the 30% discount available right now for
                first-time buyers.
              </p>

              <p data-id="element-247">If you're tired of being the last one down every winter...</p>

              <p data-id="element-248">
                If you're tired of powering through fevers while everyone else
                rests...
              </p>

              <p className="font-bold text-slate-900" data-id="element-249">
                Now's the time to try something different.
              </p>

              {/* SECTION - Special Offer */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-250">
                Special Offer For First-Time Customers
              </h2>

              <p data-id="element-251">
                For a limited time, first-time customers can save 30% on their
                first order of Glisera™ NasalShield.
              </p>

              <p data-id="element-252">No code needed. Discount applied automatically.</p>

              <p data-id="element-253">Most moms start with the 3-bottle bundle:</p>

              <img src="https://cdn.magicpatterns.com/uploads/q6VAYTGnNtqnf713rcLtDZ/ChatGPT_Image_17_Φεβ_2026,_09_23_40_μ.μ..png" alt="Glisera NasalShield 3-bottle bundle" className="w-full rounded-xl my-8 shadow-lg" data-id="element-254" />

              <div className="bg-emerald-50 p-4 md:p-6 rounded-xl border border-emerald-100 my-8 not-prose" data-id="element-255">
                <ul className="space-y-3" data-id="element-256">
                  <li className="flex items-start gap-3" data-id="element-257">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-258">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-259" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-260">
                      One for the bathroom
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-261">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-262">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-263" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-264">
                      One for your purse
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-265">
                    <div className="bg-emerald-500 rounded-full p-1 mt-0.5" data-id="element-266">
                      <Check size={12} className="text-white" strokeWidth={3} data-id="element-267" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm md:text-base" data-id="element-268">
                      One for travel
                    </span>
                  </li>
                </ul>
              </div>

              {/* Main CTA Offer Box */}
              <div id="offer-section" className="bg-white border-2 border-emerald-500 rounded-2xl p-5 md:p-10 shadow-2xl my-12 not-prose relative overflow-hidden" data-id="element-269">
                <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs font-bold px-4 py-1 rounded-bl-lg" data-id="element-270">
                  LIMITED TIME OFFER
                </div>

                <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mb-2 text-center mt-4 md:mt-0" data-id="element-271">
                  Save 30% On Glisera™ NasalShield
                </h2>
                <p className="text-center text-slate-600 mb-6 md:mb-8 text-sm md:text-base" data-id="element-272">
                  Stop germs at the front door — starting tomorrow morning
                </p>

                <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-center mb-6 md:mb-8" data-id="element-273">
                  <div className="flex-1 w-full" data-id="element-274">
                    <ul className="space-y-3" data-id="element-275">
                      <li className="flex items-center gap-2 text-slate-700 text-sm md:text-base" data-id="element-276">
                        <Check size={18} className="text-emerald-500 flex-shrink-0" data-id="element-277" />
                        Free shipping on all orders
                      </li>
                      <li className="flex items-center gap-2 text-slate-700 text-sm md:text-base" data-id="element-278">
                        <Check size={18} className="text-emerald-500 flex-shrink-0" data-id="element-279" />
                        60-day money-back guarantee
                      </li>
                      <li className="flex items-center gap-2 text-slate-700 text-sm md:text-base" data-id="element-280">
                        <Check size={18} className="text-emerald-500 flex-shrink-0" data-id="element-281" />
                        Safe, gentle formula
                      </li>
                    </ul>
                  </div>

                  <div className="flex-1 w-full" data-id="element-282">
                    <Button fullWidth size="lg" onClick={scrollToOffer} className="mb-3 shadow-emerald-200 shadow-xl" data-id="element-283">
                      <span className="hidden md:inline" data-id="element-284">
                        GET GLISERA™ NASALSHIELD — SAVE 30% NOW
                      </span>
                      <span className="md:hidden" data-id="element-285">
                        GET NASALSHIELD — 30% OFF
                      </span>
                    </Button>
                    <div className="flex items-center justify-center gap-4 text-xs text-slate-500" data-id="element-286">
                      <span className="flex items-center gap-1" data-id="element-287">
                        <Truck size={12} data-id="element-288" /> Free Shipping
                      </span>
                      <span className="flex items-center gap-1" data-id="element-289">
                        <Clock size={12} data-id="element-290" /> Limited Time
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-center text-sm text-slate-600 font-medium" data-id="element-291">
                  60-Day Money-Back Guarantee – Risk-Free
                </p>
              </div>

              {/* SECTION - Closing */}
              <h2 className="font-sans font-bold text-2xl md:text-3xl text-slate-900 mt-12 mb-6" data-id="element-292">
                You Don't Have To Be "The Sick Mom" Anymore
              </h2>

              <p data-id="element-293">You don't have to be "the mom who always gets sick."</p>

              <p data-id="element-294">You don't have to be the last domino every winter.</p>

              <p data-id="element-295">You can be the one who stays strong.</p>

              <p data-id="element-296">The one who keeps the house running.</p>

              <p className="font-bold text-slate-900" data-id="element-297">
                The one who doesn't go down.
              </p>

              <p data-id="element-298">
                Thousands of moms have already made the switch to Glisera™
                NasalShield.
              </p>

              {/* Final CTA Button */}
              <div className="not-prose my-10" data-id="element-299">
                <Button fullWidth size="lg" onClick={scrollToOffer} className="shadow-lg shadow-emerald-200" data-id="element-300">
                  <span className="hidden md:inline" data-id="element-301">
                    TRY GLISERA™ NASALSHIELD — 30% OFF TODAY →
                  </span>
                  <span className="md:hidden" data-id="element-302">TRY NASALSHIELD — 30% OFF →</span>
                </Button>
                <p className="text-center text-xs text-slate-500 mt-2" data-id="element-303">
                  Free Shipping • 60-Day Money-Back Guarantee
                </p>
              </div>
            </article>
          </main>

          {/* Sticky Sidebar CTA - Desktop Only */}
          <aside className="hidden lg:block" data-id="element-304">
            <StickyCTA onCtaClick={scrollToOffer} data-id="element-305" />
          </aside>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-200 py-8 md:py-12 px-4 mb-24 lg:mb-0" data-id="element-306">
        <div className="max-w-3xl mx-auto text-center" data-id="element-307">
          <div className="mb-6 md:mb-8" data-id="element-308">
            <div className="font-bold text-xl tracking-tight text-slate-900 mb-4" data-id="element-309">
              Family Health<span className="text-emerald-600" data-id="element-310"> Today</span>
            </div>
          </div>

          <div className="text-[10px] text-slate-400 space-y-4 leading-relaxed border-t border-slate-200 pt-6 md:pt-8" data-id="element-311">
            <p data-id="element-312">
              This product is not intended to diagnose, treat, cure, or prevent
              any disease. Results may vary. Always consult your physician
              before starting any new health routine. These statements have not
              been evaluated by the Food and Drug Administration.
            </p>
            <p data-id="element-313">
              This website is an advertisement and not an actual news article,
              blog, or consumer protection update. Any photographs of persons
              used on this site are models. The owner of this site and the
              products referred to on this site only provides a service where
              consumers can obtain and compare products.
            </p>
            <p data-id="element-314">
              Trademarks utilized on our website belong to their respective
              owners. Through research and experienced editors we provide
              feedback about products. We are independently owned. This website
              is supported by referral fees from featured products.
            </p>
            <div className="flex flex-wrap justify-center gap-3 md:gap-4 pt-4" data-id="element-315">
              <a href="#" className="hover:text-slate-600" data-id="element-316">
                About Us
              </a>
              <a href="#" className="hover:text-slate-600" data-id="element-317">
                Terms of Use
              </a>
              <a href="#" className="hover:text-slate-600" data-id="element-318">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-slate-600" data-id="element-319">
                Contact
              </a>
            </div>
            <p className="pt-4" data-id="element-320">
              ©2025 Family Health Today. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className={`fixed bottom-0 left-0 right-0 bg-white border-t-2 border-emerald-500 shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.15)] transition-transform duration-300 z-50 lg:hidden ${showStickyCTA ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-321">
        <div className="p-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]" data-id="element-322">
          <div className="flex items-center justify-between mb-3" data-id="element-323">
            <div data-id="element-324">
              <p className="font-bold text-sm text-slate-900" data-id="element-325">
                Stop Getting Sick This Winter
              </p>
              <div className="flex items-center gap-1" data-id="element-326">
                {[...Array(5)].map((_, i) => <Star key={i} size={12} fill="#FCD34D" className="text-yellow-400" data-id="element-327" />)}
                <span className="text-xs text-slate-600 ml-1" data-id="element-328">4.8 (8,247)</span>
              </div>
            </div>
            <div className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-329">
              30% OFF
            </div>
          </div>
          <Button fullWidth onClick={scrollToOffer} size="lg" data-id="element-330">
            GET NASALSHIELD →
          </Button>
          <p className="text-[10px] text-center text-slate-500 mt-2" data-id="element-331">
            60-Day Guarantee • Free Shipping
          </p>
        </div>
      </div>
    </div>;
}